/** The map's legend in a modal: what each colour, line and marker the page draws means. */
import { Group, Modal, SimpleGrid, Stack, Text, Title } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { brand } from '@/theme/theme';
import { useFormat } from '@/components/useFormat';
import { useGenusName } from './genusName';
import { legendOf, type LegendEntry, type LegendInput } from './layerCatalog';
import { LayerSample } from './LayerSample';

export interface MapLegendProps extends LegendInput {
  opened: boolean;
  onClose: () => void;
}

/** A colour scale as the map interpolates it: a bar through its stops, each stop's value under it. */
export function Scale({ entry }: { entry: LegendEntry }) {
  const { t } = useTranslation('viewer');
  const { number } = useFormat();
  const stops = entry.stops!;
  const unit = entry.range ? t(entry.range.unitKey) : '';
  const span = Math.max(1, stops.length - 1);
  // As many decimals as the step between two ticks needs.
  const step = stops.length > 1 ? Math.abs(stops[1]!.value - stops[0]!.value) : 1;
  const digits = step >= 1 || step === 0 ? 0 : step >= 0.1 ? 1 : 2;
  return (
    <Stack gap={4} data-testid={`legend-${entry.id}`} aria-label={t(entry.label)}>
      <Text fz="sm">
        {t(entry.label)}
        {unit && (
          <Text span fz="xs" c={brand.textMuted}>
            {' '}
            ({unit})
          </Text>
        )}
      </Text>
      {entry.hint && (
        <Text fz="xs" c={brand.textMuted}>
          {t(entry.hint)}
        </Text>
      )}
      <div style={{ height: 12, borderRadius: 3, border: `1px solid ${brand.border}`, background: `linear-gradient(to right, ${stops.map((s, i) => `${s.color} ${(100 * i) / span}%`).join(', ')})` }} />
      <div style={{ position: 'relative', height: 16 }}>
        {stops.map((s, i) => (
          <Text
            key={i}
            fz={11}
            c={brand.textMuted}
            data-testid="legend-tick"
            style={{ position: 'absolute', left: `${(100 * i) / span}%`, transform: i === 0 ? undefined : i === span ? 'translateX(-100%)' : 'translateX(-50%)', whiteSpace: 'nowrap' }}
          >
            {number(s.value, digits)}
          </Text>
        ))}
      </div>
    </Stack>
  );
}

function Entry({ entry }: { entry: LegendEntry }) {
  const { t } = useTranslation('viewer');
  const genus = useGenusName();
  const { number } = useFormat();
  const isGenus = entry.ns === 'reports';
  const latin = entry.id.replace(/^genus-/, '');
  const params = entry.params && Object.fromEntries(Object.entries(entry.params).map(([k, v]) => [k, (Array.isArray(v) ? v : [v]).map((n) => number(n, 1)).join(' / ')]));
  const label = isGenus ? genus(latin) : entry.level ? t(entry.label, { level: t(`feature.levels.${entry.level}`) }) : params ? t(entry.label, params) : t(entry.label);
  return (
    <Group gap="xs" wrap="nowrap" data-testid={`legend-${entry.id}`}>
      <LayerSample sample={entry.sample} />
      <Stack gap={0}>
        <Text fz="sm">
          {label}
          {isGenus && label !== latin && (
            <Text span fz="xs" c={brand.textMuted} fs="italic">
              {' '}
              {latin}
            </Text>
          )}
        </Text>
        {entry.range && (
          <Text fz="xs" c={brand.textMuted}>
            {number(entry.range.low)} – {number(entry.range.high)} {t(entry.range.unitKey)}
          </Text>
        )}
        {entry.count !== undefined && (
          <Text fz="xs" c={brand.textMuted} data-testid={`legend-count-${entry.id}`}>
            {t('legend.count', { count: entry.count, formatted: number(entry.count) })}
          </Text>
        )}
      </Stack>
    </Group>
  );
}

export function MapLegend({ opened, onClose, ...input }: MapLegendProps) {
  const { t, i18n } = useTranslation('viewer');
  const genus = useGenusName();
  const sections = opened ? legendOf(input) : [];
  return (
    <Modal opened={opened} onClose={onClose} title={t('legend.title')} size="lg">
      {!sections.length && (
        <Text fz="sm" c={brand.textMuted}>
          {t('legend.empty')}
        </Text>
      )}
      <Stack gap="lg">
        {sections.map((section) => {
          const entries = section.id === 'genera' ? [...section.entries].sort((a, b) => genus(a.id.slice(6)).localeCompare(genus(b.id.slice(6)), i18n.language)) : section.entries;
          return (
            <Stack key={section.id} gap="xs" data-testid={`legend-section-${section.id}`}>
              <Title order={4}>{t(`legend.sections.${section.id}`)}</Title>
              {section.note && (
                <Text fz="xs" c={brand.textMuted} data-testid={`legend-note-${section.id}`}>
                  {t(section.note)}
                </Text>
              )}
              {entries
                .filter((e) => e.stops)
                .map((entry) => (
                  <Scale key={entry.id} entry={entry} />
                ))}
              <SimpleGrid cols={{ base: 1, xs: 2, sm: section.id === 'genera' ? 3 : 2 }} spacing="xs" verticalSpacing={6}>
                {entries
                  .filter((e) => !e.stops)
                  .map((entry) => (
                    <Entry key={entry.id} entry={entry} />
                  ))}
              </SimpleGrid>
            </Stack>
          );
        })}
      </Stack>
    </Modal>
  );
}
